import {
  SQUAD_DEFAULTS,
  type MyProposal,
  type OpenCell,
  type PoolCellStatus,
} from '@otryad/shared';
import { shareURL } from '@telegram-apps/sdk-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { useHaptic } from '@/app/TmaProvider';
import { HoldNotice } from '@/components/HoldNotice';
import { LevelPicker } from '@/components/LevelPicker';
import { RejoinCard } from '@/components/RejoinCard';
import { TabBar } from '@/components/TabBar';
import { Button, Card, EmptyState, ErrorState, HeroCard, ListRow, Screen, Sheet, Skeleton, StatusBadge } from '@/components/ui';
import { useAppFeatures } from '@/lib/features';
import { formatMoney } from '@/lib/format';
import { inFirstSquad, poolCount } from '@/lib/poolCount';
import {
  cellLevelText,
  errorText,
  fmt,
  PLURAL_GATHERINGS,
  plural,
  texts,
  weekdayLong,
  weekdaysShort,
} from '@/lib/texts';
import { trpc } from '@/lib/trpc';

import { sizeLabel } from './onboarding/ActivitiesScreen';

const t = texts.pool;

function apiReason(e: unknown): string | undefined {
  return (e as { data?: { reason?: string } }).data?.reason;
}

/** Прогресс ячейки: крупное «4 из 6», порог, очередь, вечера. */
function MyCell({
  cell,
  onOpen,
  onWithdraw,
}: {
  cell: PoolCellStatus;
  onOpen: () => void;
  onWithdraw: () => void;
}) {
  const ready = cell.count >= cell.minToLaunch;
  const n = poolCount(cell.count, cell.squadSize);
  return (
    <HeroCard
      eyebrow={fmt(t.heroEyebrow, { activity: cell.activity.name })}
      title={fmt(t.heroOf, { count: n.shown, size: cell.squadSize })}
      meta={
        <>
          <span>
            {sizeLabel(cell.squadSize)} · {cellLevelText(cell.levelBand, cell.leveled)}
          </span>
          {n.queue > 0 ? <span>{fmt(t.cellQueue, { queue: n.queue })}</span> : null}
          <span>
            {inFirstSquad(cell.position, cell.squadSize)
              ? fmt(t.cellPosition, { position: cell.position })
              : t.cellPositionNext}
          </span>
          <span>{fmt(t.cellDays, { days: weekdaysShort(cell.days) })}</span>
          {/* Раунд персон 3 (N4): «1 из 8» без минимума читалось как «ждать ещё семерых» */}
          {ready ? (
            <span className="text-success">{t.cellReadyLong}</span>
          ) : (
            <span>{fmt(t.cellMinLine, { min: cell.minToLaunch, size: cell.squadSize })}</span>
          )}
        </>
      }
      badge={
        <StatusBadge tone={ready ? 'success' : 'accent'}>
          {n.full
            ? t.cellFull
            : ready
              ? t.cellReady
              : fmt(t.cellNeeded, { left: Math.max(0, cell.minToLaunch - cell.count) })}
        </StatusBadge>
      }
      progress={n.shown / cell.squadSize}
      progressDone={ready}
      onOpen={onOpen}
    >
      <div className="mt-3 flex items-center justify-between gap-2">
        <Button
          variant="ghost"
          onClick={(e) => {
            e.stopPropagation();
            onOpen();
          }}
        >
          {t.cellOpen}
        </Button>
        <Button
          variant="ghost"
          onClick={(e) => {
            e.stopPropagation();
            onWithdraw();
          }}
        >
          {t.withdraw}
        </Button>
      </div>
    </HeroCard>
  );
}

function ProposalRow({ p }: { p: MyProposal }) {
  const tone = p.status === 'PENDING' ? 'accent' : p.status === 'REJECTED' ? 'danger' : 'success';
  const label =
    p.status === 'PENDING'
      ? t.proposalPending
      : p.status === 'APPROVED'
        ? t.proposalApproved
        : p.status === 'MERGED'
          ? fmt(t.proposalMerged, { name: p.resultActivity?.name ?? '' })
          : t.proposalRejected;
  return (
    <ListRow
      title={p.name}
      subtitle={
        <>
          {fmt(t.proposalSizes, { sizes: p.sizeOptions.join(' / ') })}
          {p.opsComment ? ` · ${p.opsComment}` : ''}
        </>
      }
      trailing={<StatusBadge tone={tone}>{label}</StatusBadge>}
    />
  );
}

function OpenCellRow({
  cell,
  alt,
  onJoin,
  onOpen,
}: {
  cell: OpenCell;
  /** Набор той же активности, куда можно сразу (`nextCellKey`), — следующий шаг у закрытого */
  alt: OpenCell | null;
  onJoin: () => void;
  onOpen: () => void;
}) {
  const n = poolCount(cell.count, cell.squadSize);
  return (
    // Строка ведёт внутрь набора: по «Падел · вторник · 3 из 8» не решить, идти ли сюда —
    // не видно ни минимума для старта, ни районов. Кнопка «Присоединиться» гасит всплытие.
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
      className="border-border pressable flex cursor-pointer flex-col gap-2 border-b px-4 py-3 last:border-b-0"
    >
      <div className="flex items-baseline justify-between gap-3">
        <span className="min-w-0 flex-1 truncate text-base font-semibold">{cell.activity.name}</span>
        <span className="num text-fg shrink-0 text-base font-semibold">
          {fmt(t.openCellProgress, { count: n.shown, size: cell.squadSize })}
          {n.queue > 0 ? (
            <span className="text-muted ml-1.5 text-xs font-normal">
              {fmt(t.cellQueueShort, { queue: n.queue })}
            </span>
          ) : null}
        </span>
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          aria-hidden="true"
          className="text-muted shrink-0"
        >
          <path d="M6 3l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      </div>
      {/* Кнопка — отдельной строкой под описанием: рядом она сжимала текст до слова на строку (ревью 2026-09-24) */}
      <div className="flex flex-col items-start gap-2">
        {/* Уровень виден всегда (раунд 4, N2): раньше при известных районах он пропадал */}
        <span className="text-muted min-w-0 text-sm">
          {weekdayLong(cell.weekday).toLowerCase()} · {sizeLabel(cell.squadSize)} ·{' '}
          {cellLevelText(cell.levelBand, cell.leveled)}
          {cell.districtNames.length > 0
            ? ` · ${fmt(t.openCellDistricts, { districts: cell.districtNames.join(', ') })}`
            : ''}
        </span>
        {cell.isMine ? (
          <StatusBadge tone="accent">{t.yours}</StatusBadge>
        ) : cell.canJoin ? (
          <Button
            variant="secondary"
            onClick={(e) => {
              e.stopPropagation();
              onJoin();
            }}
            className="shrink-0 px-3 text-sm"
          >
            {n.full ? t.joinQueue : t.join}
          </Button>
        ) : null}
      </div>
      {/* Закрытый набор называет причину и следующий шаг; сам шаг — внутри набора */}
      {!cell.isMine && !cell.canJoin && cell.joinBlock ? (
        <p className="text-muted text-xs">
          {t.joinBlock[cell.joinBlock]}
          {alt
            ? `. ${fmt(t.joinAlt, {
                weekday: weekdayLong(alt.weekday).toLowerCase(),
                level: cellLevelText(alt.levelBand, alt.leveled),
              })}`
            : ''}
        </p>
      ) : null}
    </div>
  );
}

/** «Ищем состав» (BLUEPRINT 3.8, Stage 3 задача 3). */
export function PoolScreen() {
  const navigate = useNavigate();
  const haptic = useHaptic();
  const utils = trpc.useUtils();
  const status = trpc.pool.status.useQuery();
  const open = trpc.pool.openCells.useQuery({});
  const join = trpc.pool.join.useMutation();
  const withdraw = trpc.pool.withdraw.useMutation();

  const [joinCell, setJoinCell] = useState<OpenCell | null>(null);
  const [joinLevel, setJoinLevel] = useState<number | null>(null);
  const [needLevel, setNeedLevel] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);
  const [withdrawId, setWithdrawId] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  // Stage 17: с «Моими сборами» у человека в пуле появляется нижняя панель
  const { myGatherings } = useAppFeatures();
  const footer = myGatherings ? <TabBar active="pool" /> : undefined;

  const refresh = () => Promise.all([utils.pool.invalidate(), utils.auth.me.invalidate()]);

  const doJoin = async () => {
    if (!joinCell) return;
    setJoinError(null);
    try {
      await join.mutateAsync({
        activityId: joinCell.activity.id,
        squadSize: joinCell.squadSize,
        // Ключ выбранного набора: сервер ставит заявку в этот вечер и уровень или называет,
        // почему профиль не подходит (раунд 3, N1)
        cellKey: joinCell.cellKey,
        ...(joinLevel ? { level: joinLevel } : {}),
      });
      haptic.notify('success');
      setJoinCell(null);
      setJoinLevel(null);
      setNeedLevel(false);
      await refresh();
    } catch (e) {
      const reason = apiReason(e);
      if (reason === 'level_required') {
        setNeedLevel(true);
        return;
      }
      haptic.notify('error');
      setJoinError(errorText(reason));
    }
  };

  const doWithdraw = async () => {
    if (!withdrawId) return;
    // Диалог обещает «вернуться в набор можно в любой момент»: после снятия сервер отдаёт эту
    // активность в `status.rejoin`, и карточка «Вернуться в набор» появляется из данных (N1).
    try {
      await withdraw.mutateAsync({ applicationId: withdrawId });
      haptic.impact('medium');
      setWithdrawId(null);
      await refresh();
    } catch {
      haptic.notify('error');
    }
  };

  const share = (link: string) => {
    if (shareURL.isAvailable()) {
      shareURL(link, t.referralShareText);
      return;
    }
    void navigator.clipboard?.writeText(link).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (status.isError) {
    return (
      <Screen title={t.title} footer={footer}>
        <ErrorState onRetry={() => void status.refetch()} />
      </Screen>
    );
  }

  const data = status.data;
  // Раунд персон 3 (N9): свои наборы уже стоят карточками выше — в списке города их не дублируем
  const othersCells = (open.data ?? []).filter((c) => !c.isMine);

  return (
    <Screen
      title={t.title}
      subtitle={t.subtitle}
      footer={footer}
      action={
        <Button variant="ghost" onClick={() => void navigate('/profile')}>
          {t.profile}
        </Button>
      }
    >
      {/* Раунд персон 3 (M4): бронь с отсчётом — и там, где человек без отряда открывает приложение */}
      <HoldNotice />

      {!data ? (
        <>
          <Skeleton className="h-56" />
          <Skeleton className="h-28" />
        </>
      ) : (
        <>
          {data.cells.length > 0 ? (
            data.cells.map((c) => (
              <MyCell
                key={c.applicationId}
                cell={c}
                onOpen={() => void navigate(`/pool/cell/${c.applicationId}`)}
                onWithdraw={() => setWithdrawId(c.applicationId)}
              />
            ))
          ) : data.rejoin.length > 0 || data.proposals.some((p) => p.status === 'PENDING') ? null : (
            <Card>
              <EmptyState
                title={t.noCells}
                body={t.noCellsBody}
                actionLabel={t.toOnboarding}
                onAction={() => void navigate('/onboarding/activities')}
              />
            </Card>
          )}

          <RejoinCard
            items={data.rejoin}
            slotsLeft={data.slotsLeft}
            primary={data.cells.length === 0}
          />

          {data.proposals.length > 0 ? (
            <Card title={t.proposalsTitle} flush>
              {data.proposals.map((p) => (
                <ProposalRow key={p.id} p={p} />
              ))}
            </Card>
          ) : null}

          <Card title={t.whatNext}>
            <ol className="flex list-decimal flex-col gap-1.5 pl-5 text-sm">
              {t.whatNextSteps.map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ol>
            {/* Порядок цены до приглашения (раунд 3, N3): без него предоплата сезона приходила
                сюрпризом в сообщении бота. Ориентир — самая низкая цена среди своих наборов. */}
            {data.cells.length > 0 ? (
              <p className="text-muted mt-3 text-sm">
                {fmt(t.priceHint, {
                  count: plural(SQUAD_DEFAULTS.gatheringsPerSeason, PLURAL_GATHERINGS),
                  price: formatMoney(Math.min(...data.cells.map((c) => c.seasonPriceKopecks))),
                })}
              </p>
            ) : null}
          </Card>

          <Card title={t.openCells} flush>
            <p className="text-muted px-4 pb-2 text-sm">{t.openCellsBody}</p>
            {open.isPending ? (
              <div className="px-4 pb-4">
                <Skeleton className="h-12" />
              </div>
            ) : open.isError ? (
              <ErrorState onRetry={() => void open.refetch()} />
            ) : othersCells.length === 0 ? (
              <p className="text-muted px-4 pb-4 text-sm">{t.noOpenCells}</p>
            ) : (
              othersCells.map((c) => (
                <OpenCellRow
                  key={c.cellKey}
                  cell={c}
                  alt={open.data.find((x) => x.cellKey === c.nextCellKey) ?? null}
                  onOpen={() => void navigate(`/pool/open/${encodeURIComponent(c.cellKey)}`)}
                  onJoin={() => {
                    setJoinCell(c);
                    setJoinLevel(null);
                    // Уровень спрашиваем сразу, если его нет: иначе первое нажатие «В набор»
                    // уходило на сервер за `level_required`, и шторка молча дорастала.
                    setNeedLevel(c.needsLevel);
                    setJoinError(null);
                  }}
                />
              ))
            )}
            {/* Раунд персон 3 (N9): своя заявка раскладывалась по вечерам в четыре строки «Твой набор» */}
            {open.data && othersCells.length < open.data.length ? (
              <p className="text-muted px-4 pb-2 text-sm">{t.openCellsMine}</p>
            ) : null}
            {data.slotsLeft === 0 ? (
              <p className="text-muted px-4 pb-4 text-xs">{t.limitReached}</p>
            ) : null}
          </Card>

          <Card title={t.speedUp} flush>
            <div className="px-4 pb-3">
              <div className="text-base font-medium">{t.referralTitle}</div>
              <p className="text-muted mt-0.5 mb-3 text-sm">{t.referralBody}</p>
              {data.referralLink ? (
                // Сама ссылка не показывается: читать её незачем, а место она занимала целой
                // строкой моноширинного текста. Кнопка отправляет её или кладёт в буфер.
                <Button fullWidth onClick={() => share(data.referralLink!)}>
                  {copied
                    ? texts.common.copied
                    : shareURL.isAvailable()
                      ? texts.common.share
                      : texts.common.copy}
                </Button>
              ) : (
                <p className="text-muted text-sm">{t.referralNoBot}</p>
              )}
            </div>
            {data.features.openSeats ? (
              <ListRow
                title={t.openSeatsTitle}
                subtitle={t.openSeatsShort}
                onClick={() => void navigate('/open-seats')}
              />
            ) : null}
            {data.features.trial ? (
              <ListRow
                title={t.trialTitle}
                subtitle={t.trialShort}
                onClick={() => void navigate('/open-seats?kind=TRIAL')}
              />
            ) : null}
          </Card>

          <Card flush>
            <ListRow title={t.inviteCta} onClick={() => void navigate('/invite')} />
            <ListRow title={t.propose} onClick={() => void navigate('/pool/propose')} />
            <ListRow title={t.profile} onClick={() => void navigate('/profile')} />
          </Card>
        </>
      )}

      <Sheet
        open={joinCell !== null}
        onClose={() => setJoinCell(null)}
        title={t.joinTitle}
        footer={
          <>
            <Button
              fullWidth
              loading={join.isPending}
              disabled={needLevel && !joinLevel}
              onClick={() => void doJoin()}
            >
              {t.joinConfirm}
            </Button>
            <Button variant="ghost" fullWidth onClick={() => setJoinCell(null)}>
              {texts.common.cancel}
            </Button>
          </>
        }
      >
        {joinCell ? (
          <div className="flex flex-col gap-3">
            <p className="text-muted text-sm">
              {fmt(t.joinBody, {
                activity: joinCell.activity.name,
                size: joinCell.squadSize,
                weekday: weekdayLong(joinCell.weekday).toLowerCase(),
              })}
            </p>
            {needLevel ? (
              <>
                <p className="text-sm font-semibold">
                  {fmt(t.joinLevel, { activity: joinCell.activity.name })}
                </p>
                <LevelPicker
                  activityCode={joinCell.activity.code}
                  value={joinLevel}
                  onChange={setJoinLevel}
                />
              </>
            ) : null}
            {joinError ? (
              <p className="text-danger text-sm" role="alert">
                {joinError}
              </p>
            ) : null}
          </div>
        ) : null}
      </Sheet>

      <Sheet
        open={withdrawId !== null}
        onClose={() => setWithdrawId(null)}
        title={t.withdrawTitle}
        footer={
          <>
            <Button
              variant="danger"
              fullWidth
              loading={withdraw.isPending}
              onClick={() => void doWithdraw()}
            >
              {t.withdrawConfirm}
            </Button>
            <Button variant="ghost" fullWidth onClick={() => setWithdrawId(null)}>
              {texts.common.cancel}
            </Button>
          </>
        }
      >
        <p className="text-muted text-sm">{t.withdrawBody}</p>
      </Sheet>
    </Screen>
  );
}
